/* Engineering-blueprint ramp preview component (Wing Ramp).
   Labels are placed OUTSIDE the geometry to avoid overlaps.
   Style: dark navy plotting paper with cyan dimension lines and orange ramp body.
*/
import React from "react";

export default function RampPreview({
  width = 900, depth = 1200,
  leftRise, rightRise, left_rise, right_rise,
  leftWing, rightWing, left_wing, right_wing,
  colour = "Natural Black",
  leftKerb, rightKerb, left_kerb, right_kerb,
  curved = false,
}) {
  // Accept either camelCase or snake_case input keys
  const LR_raw = leftRise ?? left_rise ?? 150;
  const RR_raw = rightRise ?? right_rise ?? 150;
  const LW_raw = leftWing ?? left_wing ?? 0;
  const RW_raw = rightWing ?? right_wing ?? 0;
  const lKerb = leftKerb ?? left_kerb ?? false;
  const rKerb = rightKerb ?? right_kerb ?? false;

  const W  = Math.max(Number(width)  || 0, 100);
  const D  = Math.max(Number(depth)  || 0, 100);
  const LW = Math.max(Number(LW_raw)  || 0, 0);
  const RW = Math.max(Number(RW_raw) || 0, 0);
  const LR = Math.max(Number(LR_raw)  || 0, 0);
  const RR = Math.max(Number(RR_raw) || 0, 0);

  // Canvas
  const CW = 600, CH = 340;
  const PAD_L = 80, PAD_R = 50, PAD_T = 50, PAD_B = 90;
  const innerW = CW - PAD_L - PAD_R;
  const innerH = CH - PAD_T - PAD_B;
  const totalW = W + LW + RW;
  const scale = Math.min(innerW / totalW, innerH / D);

  const sw  = W  * scale;
  const sd  = D  * scale;
  const slw = LW * scale;
  const srw = RW * scale;
  const totalSW = slw + sw + srw;
  const startX = PAD_L + (innerW - totalSW) / 2;
  const topY = PAD_T;

  const cMap = {
    "Natural Black": "#1f2330", "Grey": "#6b7280", "Brown": "#7c5530",
    "Terra Cotta": "#b94f2f", "Gold": "#c89a3a", "Cream": "#e8d8b0",
  };
  const fill = cMap[colour] || "#1f2330";

  // dimension positions (below + side)
  const dimY = topY + sd + 28;     // below ramp dimension line
  const sideDimX = startX - 22;    // left depth dimension line

  return (
    <svg viewBox={`0 0 ${CW} ${CH}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet" style={{ maxHeight: 340, background: "#0a1128", borderRadius: 6 }} data-testid="ramp-preview">
      <defs>
        {/* Blueprint grid */}
        <pattern id="bpgrid" width="20" height="20" patternUnits="userSpaceOnUse">
          <path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(120,164,255,0.10)" strokeWidth="0.6" />
        </pattern>
        <pattern id="bpgrid-major" width="100" height="100" patternUnits="userSpaceOnUse">
          <path d="M 100 0 L 0 0 0 100" fill="none" stroke="rgba(120,164,255,0.20)" strokeWidth="0.8" />
        </pattern>
        <linearGradient id="ramp-fill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={fill} stopOpacity="1" />
          <stop offset="100%" stopColor={fill} stopOpacity="0.78" />
        </linearGradient>
        <marker id="arr-l" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
          <path d="M0,4 L8,0 L8,8 z" fill="#fda500" />
        </marker>
        <marker id="arr-r" markerWidth="8" markerHeight="8" refX="2" refY="4" orient="auto">
          <path d="M8,4 L0,0 L0,8 z" fill="#fda500" />
        </marker>
      </defs>

      <rect width={CW} height={CH} fill="url(#bpgrid)" />
      <rect width={CW} height={CH} fill="url(#bpgrid-major)" />

      {/* Corner registration marks */}
      <g stroke="#fda500" strokeWidth="0.8" opacity="0.6">
        <path d={`M0,16 L0,0 L16,0`} fill="none" />
        <path d={`M${CW-16},0 L${CW},0 L${CW},16`} fill="none" />
        <path d={`M0,${CH-16} L0,${CH} L16,${CH}`} fill="none" />
        <path d={`M${CW-16},${CH} L${CW},${CH} L${CW},${CH-16}`} fill="none" />
      </g>

      {/* Title block */}
      <g>
        <text x={PAD_L} y={22} fontSize="9" fill="#fda500" fontFamily="'JetBrains Mono', 'IBM Plex Mono', monospace" letterSpacing="2">
          PLAN VIEW · WING RAMP
        </text>
        <text x={CW - PAD_R} y={22} fontSize="9" fill="#7da2ff" fontFamily="'JetBrains Mono', monospace" textAnchor="end">
          1:scale · mm
        </text>
      </g>

      {/* LHS Wing */}
      {LW > 0 && (
        <polygon
          points={`${startX},${topY + sd} ${startX + slw},${topY + sd} ${startX + slw},${topY}`}
          fill="url(#ramp-fill)" stroke="#fda500" strokeWidth="1.2"
        />
      )}
      {/* Main ramp */}
      <rect
        x={startX + slw} y={topY} width={sw} height={sd}
        fill="url(#ramp-fill)" stroke="#fda500" strokeWidth="1.5"
        rx={curved ? 14 : 0}
      />
      {/* RHS Wing */}
      {RW > 0 && (
        <polygon
          points={`${startX + slw + sw},${topY} ${startX + slw + sw + srw},${topY + sd} ${startX + slw + sw},${topY + sd}`}
          fill="url(#ramp-fill)" stroke="#fda500" strokeWidth="1.2"
        />
      )}

      {/* Kerbs */}
      {lKerb && <rect x={startX + slw - 5} y={topY - 4} width={5} height={sd + 8} fill="#fda500" />}
      {rKerb && <rect x={startX + slw + sw} y={topY - 4} width={5} height={sd + 8} fill="#fda500" />}

      {/* WIDTH dimension (below ramp) */}
      <g stroke="#7da2ff" strokeWidth="0.8" fill="none">
        <line x1={startX + slw} y1={topY + sd + 4} x2={startX + slw} y2={dimY + 6} />
        <line x1={startX + slw + sw} y1={topY + sd + 4} x2={startX + slw + sw} y2={dimY + 6} />
        <line x1={startX + slw} y1={dimY} x2={startX + slw + sw} y2={dimY} markerStart="url(#arr-l)" markerEnd="url(#arr-r)" stroke="#fda500" strokeWidth="1" />
      </g>
      {/* W label background + text (rendered above the line) */}
      <rect x={startX + slw + sw / 2 - 38} y={dimY - 18} width={76} height={14} fill="#0a1128" />
      <text x={startX + slw + sw / 2} y={dimY - 8} fontSize="10" fill="#fda500" fontFamily="'JetBrains Mono', monospace" textAnchor="middle" fontWeight="700">
        W = {Math.round(W)} mm
      </text>

      {/* LHS WING dimension (below) */}
      {LW > 0 && (
        <>
          <line x1={startX} y1={topY + sd + 4} x2={startX} y2={dimY + 22} stroke="#7da2ff" strokeWidth="0.8" />
          <line x1={startX + slw} y1={dimY + 16} x2={startX + slw} y2={dimY + 22} stroke="#7da2ff" strokeWidth="0.8" />
          <line x1={startX} y1={dimY + 16} x2={startX + slw} y2={dimY + 16} stroke="#7da2ff" strokeWidth="1" markerStart="url(#arr-l)" markerEnd="url(#arr-r)" />
          <rect x={startX + slw / 2 - 28} y={dimY + 24} width={56} height={12} fill="#0a1128" />
          <text x={startX + slw / 2} y={dimY + 33} fontSize="9" fill="#7da2ff" fontFamily="'JetBrains Mono', monospace" textAnchor="middle">
            LW {Math.round(LW)}
          </text>
        </>
      )}
      {/* RHS WING dimension (below) */}
      {RW > 0 && (
        <>
          <line x1={startX + slw + sw + srw} y1={topY + sd + 4} x2={startX + slw + sw + srw} y2={dimY + 22} stroke="#7da2ff" strokeWidth="0.8" />
          <line x1={startX + slw + sw + srw} y1={dimY + 16} x2={startX + slw + sw} y2={dimY + 16} stroke="#7da2ff" strokeWidth="1" markerStart="url(#arr-l)" markerEnd="url(#arr-r)" />
          <rect x={startX + slw + sw + srw / 2 - 28} y={dimY + 24} width={56} height={12} fill="#0a1128" />
          <text x={startX + slw + sw + srw / 2} y={dimY + 33} fontSize="9" fill="#7da2ff" fontFamily="'JetBrains Mono', monospace" textAnchor="middle">
            RW {Math.round(RW)}
          </text>
        </>
      )}

      {/* DEPTH dimension (left side) */}
      <g stroke="#7da2ff" strokeWidth="0.8" fill="none">
        <line x1={startX - 4} y1={topY} x2={sideDimX - 6} y2={topY} />
        <line x1={startX - 4} y1={topY + sd} x2={sideDimX - 6} y2={topY + sd} />
        <line x1={sideDimX} y1={topY} x2={sideDimX} y2={topY + sd} stroke="#fda500" strokeWidth="1" markerStart={`url(#arr-l)`} markerEnd={`url(#arr-r)`} />
      </g>
      <rect x={sideDimX - 22} y={topY + sd / 2 - 38} width={20} height={76} fill="#0a1128" />
      <text x={sideDimX - 12} y={topY + sd / 2 + 4} fontSize="10" fill="#fda500" fontFamily="'JetBrains Mono', monospace" textAnchor="middle" fontWeight="700" transform={`rotate(-90 ${sideDimX - 12} ${topY + sd / 2})`}>
        D = {Math.round(D)} mm
      </text>

      {/* Rise callouts (top right of canvas) */}
      <g>
        <text x={startX + slw + 4} y={topY - 8} fontSize="9" fill="#fda500" fontFamily="'JetBrains Mono', monospace">⌀ L.Rise {Math.round(LR)}mm</text>
        <text x={startX + slw + sw - 4} y={topY - 8} fontSize="9" fill="#fda500" fontFamily="'JetBrains Mono', monospace" textAnchor="end">⌀ R.Rise {Math.round(RR)}mm</text>
      </g>

      {/* Legend / spec footer */}
      <g>
        <text x={PAD_L} y={CH - 12} fontSize="9" fill="#7da2ff" fontFamily="'JetBrains Mono', monospace">
          MATERIAL · RUBBER · {colour.toUpperCase()}{curved ? "  ·  CURVED" : ""}{lKerb ? "  ·  L-KERB" : ""}{rKerb ? "  ·  R-KERB" : ""}
        </text>
        <text x={CW - PAD_R} y={CH - 12} fontSize="9" fill="#fda500" fontFamily="'JetBrains Mono', monospace" textAnchor="end">EAS · DRAWING REV.A</text>
      </g>
    </svg>
  );
}
